import { Suspense } from 'react';
import { Skeleton } from '@sara/ui/components/skeleton';
import { QueryErrorBoundary } from '@sara/ui/components/query-error-boundary';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  getGetExtractionQueryOptions,
  getGetExtractionQueryKey,
  getListExtractionsQueryKey,
  useCreateExtractionVersion,
} from '@sara/api-client';
import { ExtractionForm, type ExtractionFormValues } from '../../components/ExtractionForm';
import { toSuspense } from '../../../../lib/query';
import { useOrgSlug } from '../../../auth';

interface CreateExtractionVersionFormContainerProps {
  extractionId: string;
}

function CreateExtractionVersionFormSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-32 rounded-lg" />
      <Skeleton className="h-72 rounded-lg" />
      <Skeleton className="h-72 rounded-lg" />
    </div>
  );
}

function CreateExtractionVersionFormContainerContent({
  extractionId,
}: CreateExtractionVersionFormContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgSlug = useOrgSlug();

  const { data: extraction } = useSuspenseQuery(
    toSuspense(getGetExtractionQueryOptions(extractionId))
  );
  const latestVersion = extraction.versions[0] ?? null;

  const defaultValues: Partial<ExtractionFormValues> | undefined = latestVersion
    ? {
        name: extraction.name,
        description: extraction.description ?? '',
        prompt: latestVersion.prompt,
        outputSchema: JSON.stringify(latestVersion.outputSchema, null, 2),
      }
    : undefined;

  const { mutate, isPending, error } = useCreateExtractionVersion({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetExtractionQueryKey(extractionId),
        });
        void queryClient.invalidateQueries({
          queryKey: getListExtractionsQueryKey(),
        });
        void navigate({
          to: '/orgs/$orgSlug/extractions/$extractionId',
          params: { orgSlug, extractionId },
        });
      },
    },
  });

  function handleSubmit(values: ExtractionFormValues) {
    mutate({
      id: extractionId,
      data: {
        prompt: values.prompt,
        outputSchema: JSON.parse(values.outputSchema) as Record<string, unknown>,
      },
    });
  }

  return (
    <ExtractionForm
      mode="newVersion"
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isPending={isPending}
      error={error ? (error instanceof Error ? error.message : 'Failed to create version') : null}
    />
  );
}

export function CreateExtractionVersionFormContainer({
  extractionId,
}: CreateExtractionVersionFormContainerProps) {
  return (
    <QueryErrorBoundary>
      <Suspense fallback={<CreateExtractionVersionFormSkeleton />}>
        <CreateExtractionVersionFormContainerContent extractionId={extractionId} />
      </Suspense>
    </QueryErrorBoundary>
  );
}
